import { useState, useEffect } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { orpc } from '@/lib/orpc-client'
import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Member {
  userId: string
  name: string | null
  avatarUrl: string | null
}

interface TaskAssigneeSelectProps {
  projectId: string
  selectedIds: string[]
  onChange: (ids: string[]) => void
  /**
   * Members who are always selected and cannot be removed — the creator of a
   * HUB meeting, who is by definition in it (PLAN-085 §1.3).
   */
  lockedIds?: string[]
  /** Overrides the `tasks` namespace copy when the picker is reused. */
  placeholder?: string
}

export function TaskAssigneeSelect({
  projectId,
  selectedIds,
  onChange,
  lockedIds = [],
  placeholder,
}: TaskAssigneeSelectProps) {
  const { t } = useTranslation('tasks')
  const [members, setMembers] = useState<Member[]>([])
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  // Always fetch members on mount — needed for the trigger avatar display
  useEffect(() => {
    orpc.members
      .list({ projectId })
      .then((res) => setMembers(res.members))
      .catch(() => {})
  }, [projectId])

  const filtered = members.filter(
    (m) => !search || (m.name ?? '').toLowerCase().includes(search.toLowerCase())
  )

  const toggle = (userId: string) => {
    if (lockedIds.includes(userId)) return
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter((id) => id !== userId))
    } else {
      onChange([...selectedIds, userId])
    }
  }

  const selectedMembers = members.filter((m) => selectedIds.includes(m.userId))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm w-full text-left hover:bg-accent/50 transition-colors min-h-[38px]',
            selectedIds.length === 0 && 'text-muted-foreground'
          )}
        >
          {selectedIds.length === 0 ? (
            <>
              <Users className="size-4 shrink-0" />
              <span>{placeholder ?? t('assignee.placeholder')}</span>
            </>
          ) : selectedMembers.length > 0 ? (
            <>
              <div className="flex -space-x-1.5">
                {selectedMembers.slice(0, 3).map((m) => (
                  <Avatar key={m.userId} className="size-6 border-2 border-background">
                    {m.avatarUrl && <AvatarImage src={m.avatarUrl} />}
                    <AvatarFallback className="text-[9px]">
                      {(m.name ?? '?')[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              {selectedIds.length > 3 && (
                <span className="text-xs text-muted-foreground ml-1">
                  +{selectedIds.length - 3}
                </span>
              )}
              <span className="text-xs text-muted-foreground ml-1 truncate">
                {selectedMembers.map((m) => m.name ?? m.userId).join(', ')}
              </span>
            </>
          ) : (
            <>
              <Users className="size-4 shrink-0" />
              <span>{t('assignee.selected', { count: selectedIds.length })}</span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-2">
          <Input
            placeholder={t('assignee.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="max-h-48 overflow-y-auto px-1 pb-2">
          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              {t('assignee.noMatches')}
            </p>
          )}
          {filtered.map((m) => (
            <button
              key={m.userId}
              type="button"
              disabled={lockedIds.includes(m.userId)}
              className={cn(
                'flex items-center gap-2 w-full px-2 py-1.5 rounded-md transition-colors text-left',
                lockedIds.includes(m.userId) ? 'opacity-60' : 'hover:bg-accent'
              )}
              onClick={() => toggle(m.userId)}
            >
              <Checkbox checked={selectedIds.includes(m.userId)} className="pointer-events-none" />
              <Avatar className="size-5">
                {m.avatarUrl && <AvatarImage src={m.avatarUrl} />}
                <AvatarFallback className="text-[9px]">
                  {(m.name ?? '?')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm truncate">{m.name ?? m.userId}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
